import { environment } from '@env/environment';

/**
 * Where the deployed bundle reads its Cognito settings from.
 *
 * Must stay in step with `RUNTIME_CONFIG_KEY` in
 * `aws/lib/frontend/frontend_site.ts`, which writes this file into the site bucket
 * during `cdk deploy`. It cannot come from `environment.*.ts`: `ng build` inlines
 * those at compile time, and the pool's ids only exist once CloudFormation has
 * created it.
 */
export const RUNTIME_CONFIG_PATH = 'assets/config.json';

/** The Cognito user pool the browser signs users in against. */
export interface CognitoConfig {
  userPoolId: string;
  userPoolClientId: string;
  region: string;
  /**
   * Hostname of the Cognito-hosted domain Google signs users back through. Google is the
   * only way to sign in, so without it the login page reports sign-in as unconfigured.
   */
  oauthDomain?: string;
}

/** Shape of the deployed `assets/config.json`. */
export interface RuntimeConfig {
  cognito: CognitoConfig;
}

/**
 * Amplify's `assertTokenProviderConfig` requires both ids to be truthy and rejects
 * every auth call before it builds a request when either is blank. That is exactly
 * what shipped to staging: empty placeholder ids produced a failed signup with no
 * network activity and nothing in the console, so treat blank as unconfigured here
 * and say so out loud rather than handing Amplify a config it will silently reject.
 */
export const isCognitoConfigured = (
  config: Partial<CognitoConfig> | null | undefined
): config is CognitoConfig => !!config?.userPoolId && !!config?.userPoolClientId;

/**
 * Resolves the Cognito settings for this environment, preferring the deploy-time
 * file and falling back to the compiled-in environment (which is how `ng serve`
 * against a local pool works). Returns null when neither is usable — the caller
 * bootstraps anyway so the public pages still render, and the auth screens report a
 * configuration problem instead of a generic failure.
 */
export const loadCognitoConfig = async (
  fetcher: typeof fetch = (input, init) => fetch(input, init)
): Promise<CognitoConfig | null> => {
  try {
    const response = await fetcher(RUNTIME_CONFIG_PATH, { cache: 'no-cache' });
    if (response.ok) {
      const { cognito } = (await response.json()) as Partial<RuntimeConfig>;
      if (isCognitoConfigured(cognito)) {
        return cognito;
      }
      console.error(
        `${RUNTIME_CONFIG_PATH} is missing userPoolId or userPoolClientId. ` +
          'Sign-up and sign-in will not work until the frontend stack is redeployed.'
      );
    } else {
      console.error(`${RUNTIME_CONFIG_PATH} responded ${response.status}.`);
    }
  } catch (error) {
    console.error(`Could not read ${RUNTIME_CONFIG_PATH}.`, error);
  }

  if (isCognitoConfigured(environment.cognito)) {
    return environment.cognito;
  }

  console.error('No Cognito configuration available; authentication is disabled for this session.');
  return null;
};
